/**
 * Built-in `ROW_TYPE_SPECS` population (Phase 3d).
 *
 * Migrates the tree-relevant slice of per-variant metadata from
 * `tento/tento-lona-js/src/row-type.ts` into the tento/tento-lona-js/sheets registry:
 *
 *   - `defaultAttributes` (JSON instance defaults)
 *   - `childSpecs` (declarative child seeds)
 *   - `subsumedBy` (layout-driven row hiding)
 *   - `activeLayout` (parent → layout key for subsumption)
 *   - `rendersChildren` (collapse hook)
 *   - `heightBounds` (setHeight clamp)
 *
 * Rendering / presentation fields (`layoutSpec`, `cellLayoutOptions`,
 * `buildLabel`, `buildAriaLabel`, `createVariant`, `dataOwner`,
 * `nestable`, `previewImageUrl`, …) stay on lona-js's `Descriptor`
 * for now — they belong with the rendering layer port (Phase 4+).
 *
 * The registry is mutable and self-registers at module load via the
 * side effect at the bottom of this file. `raw-sheet.ts` imports
 * this module for its side effect so the registry is populated
 * before any `RawSheet.hydrate` call reads it.
 */

import { Rational } from "@tento-core/rational";

import { Alias, Inert, Layout, Source } from "../@generated/row-type-catalog.ts";
import type { RowLayoutShape } from "../render/layout-types.ts";
import {
  type ChildSpec,
  rowTypeSpecRegistry,
  type RowTypeSpec,
} from "./row-type-spec.ts";

/** Height clamp used by `Row.setHeight` for resizable row types. */
const DEFAULT_HEIGHT_BOUNDS = { min: 24, max: 960 };

/**
 * Logical shape constants. Each value mirrors the
 * `stackable / spannable / minWidthTimescale` triple from the
 * matching DOM `Descriptor.X.layoutSpec(...)` in
 * `tento/tento-lona-js/src/rows/row-descriptor.ts`. Pixel heights stay DOM-side.
 */
const SHAPE_TEXT = (): RowLayoutShape => ({
  stackable: true,
  spannable: true,
  // Render per day column in day view (mirrors the DOM Text descriptor) rather
  // than a week-wide span-stacked strip.
  minWidthTimescale: null,
});

const SHAPE_NUMBER_LIKE = (): RowLayoutShape => ({
  stackable: true,
  spannable: true,
  minWidthTimescale: null,
});

const SHAPE_FORMULA = (): RowLayoutShape => ({
  stackable: false,
  spannable: true,
  minWidthTimescale: null,
});

const SHAPE_WEEK_SPAN = (): RowLayoutShape => ({
  stackable: false,
  spannable: true,
  minWidthTimescale: "week",
});

// ── Per-type specs ────────────────────────────────────────────────

// Pixel heights are owned by the DOM-side descriptor at
// `tento/tento-lona-js/src/rows/row-descriptor.ts` via `simple()`'s
// `opts.layout` (or hand-written `layoutSpec` callers); rows-side
// is the source of truth for `layoutShape` only, since shape is what
// rows-side tree flattening / CLI consumers need.

const CALENDAR_LIST: RowTypeSpec = {
  layoutShape: SHAPE_WEEK_SPAN,
};

const TIMELINE_CHILD_SPECS: readonly ChildSpec[] = [
  {
    preset: "timeline-renderer-0",
    type: "column.timeline",
    position: Rational.nth(0),
    storage: "parent",
    placement: "child",
  },
];

const TIMELINE: RowTypeSpec = {
  activeLayout: () => "timeline",
  defaultAttributes() {
    return { children: { "timeline-renderer-0": { visible: false } } };
  },
  childSpecs: () => TIMELINE_CHILD_SPECS,
};

// Wire types registered under the canonical layout ids — used by
// the activeLayout-driven hydration synthesis in
// `alias-templates.ts:expandAliasInstance`. When an alias template
// declares `activeLayout: () => "timeline[column]"`, the hydrator
// synthesizes a child row of that wire type. Its spec returns the
// same string so descendants whose `subsumedBy` includes it get
// subsumed correctly at flatten time.
const TIMELINE_COLUMN_LAYOUT: RowTypeSpec = {
  activeLayout: () => "timeline[column]",
};

const TIMELINE_ROW_LAYOUT: RowTypeSpec = {
  activeLayout: () => "timeline[row]",
};

const GRID_LAYOUT: RowTypeSpec = {
  activeLayout: () => "grid",
};

const CANVAS_LAYOUT: RowTypeSpec = {
  activeLayout: () => "canvas",
};

// Calendar body: one stable layout row whose render is timescale-
// polymorphic — the `calendar-body` plugin paints the timed timeline +
// all-day strip for week and the month grid for month. The event
// renderers (`grid.event` + `timeline[column].event`) coexist as
// children; the plugin selects which to paint, so nothing is subsumed
// by timescale. Replaces the template-swapped `timeline[column]` ⇄
// `grid` bodies so the layout survives hydration (forking).
const CALENDAR_BODY_LAYOUT: RowTypeSpec = {
  // Render-time polymorphism: the body ROW is stable (`calendar-body`), but
  // its active layout for SUBSUMPTION switches by timescale. Flatten subsumes
  // the events source under the layout whose prefix matches one of its
  // renderer ids (`isSubsumedUnderLayout` → `hasRendererWithPrefix`): month →
  // `grid` (subsumes via `grid.event`), week/day → `timeline[column]`
  // (subsumes via `timeline[column].event`). A static `"calendar-body"` here
  // matches NO renderer prefix, so the events source isn't subsumed and BOTH
  // renderers paint (double events). The switch is at the flatten (render)
  // level — not the childspec — so it survives hydration (forking).
  activeLayout: (timescale) =>
    timescale === "month" ? "grid" : "timeline[column]",
};

const LIST: RowTypeSpec = {};

const CALENDAR_SYSTEM: RowTypeSpec = {
  rendersChildren: () => false,
};

// Weather DOM descriptor uses `simple("weather", {...})` with no
// `layout` override, so it inherits `DEFAULT_LAYOUT` (no stacking,
// no spanning, default `cellHeight: 32`). Both shape and heights
// stay neutral.
const WEATHER: RowTypeSpec = {};

// NOTE: Data's `childSpecs` in lona-js are seeded
// from `DATA_PRESETS` (hrule, points, regression,
// etc.). Several of those child types lack DESCRIPTOR_BUILDERS
// entries in tento/tento-lona-js/sheets, so the full port lands with 3c's
// renderer-table completion. Land `ROW_TYPE_SPECS.data` as a
// shell for now so 3a can still look up a spec by wireType.
const DATA: RowTypeSpec = {
  heightBounds: DEFAULT_HEIGHT_BOUNDS,
};
const DATA_V1: RowTypeSpec = {
  heightBounds: DEFAULT_HEIGHT_BOUNDS,
  layoutShape: SHAPE_WEEK_SPAN,
};

const TIMEZONE: RowTypeSpec = {
  defaultMetadata: () => ({ shape: "timezone/v1", encoding: "timezone.v1" }),
};

const STICKER: RowTypeSpec = {};

// A bare `type:"surface"` row (e.g. the body wrapper authored by
// raw-sheet templates) carries no wire `attributes.surface`. The
// inert surface plugin requires one of "header" | "body" | "footer";
// seed the natural default ("body", the main content region) here so
// reconciliation fills it in. Explicit header/footer surface rows keep
// their value (instance wins over defaults in `reconcileAttributes`).
const SURFACE: RowTypeSpec = {
  defaultAttributes: () => ({ surface: "body" }),
};

const TEXT: RowTypeSpec = {
  heightBounds: DEFAULT_HEIGHT_BOUNDS,
  layoutShape: SHAPE_TEXT,
};
const NUMBER: RowTypeSpec = {
  heightBounds: DEFAULT_HEIGHT_BOUNDS,
  layoutShape: SHAPE_NUMBER_LIKE,
};
const CHECKMARK: RowTypeSpec = {
  heightBounds: DEFAULT_HEIGHT_BOUNDS,
  layoutShape: SHAPE_NUMBER_LIKE,
};
const FORMULA: RowTypeSpec = {
  heightBounds: DEFAULT_HEIGHT_BOUNDS,
  layoutShape: SHAPE_FORMULA,
};
const TASKS: RowTypeSpec = {
  defaultMetadata: () => ({ shape: "task/v1", encoding: "task.v1" }),
  heightBounds: DEFAULT_HEIGHT_BOUNDS,
  layoutShape: SHAPE_WEEK_SPAN,
};
const LABEL: RowTypeSpec = {
  heightBounds: DEFAULT_HEIGHT_BOUNDS,
};
const LOGS: RowTypeSpec = {
  layoutShape: SHAPE_WEEK_SPAN,
  // `logs/v1` shape drives the default renderer: the source materializes
  // `column.logs` via `resolveDefaultRendererId` (shape-keyed) at hydrate —
  // no explicit childSpec renderer needed (that would double-hydrate).
  defaultMetadata: () => ({ shape: "logs/v1", encoding: "logs.v1" }),
};
const VIDEO: RowTypeSpec = {
  heightBounds: DEFAULT_HEIGHT_BOUNDS,
  layoutShape: SHAPE_WEEK_SPAN,
};

// ── Bulk registration ─────────────────────────────────────────────

// Canonical keys reference the generated row-type catalog
// (`@generated/row-type-catalog`). Layout aliases that remain outside the
// catalog (`timeline`, `timeline-row`, `timeline-column`, `calendar-system`,
// `list`) are rows-side structural helpers rather than persisted row types.
const BUILTIN_ENTRIES: ReadonlyArray<readonly [string, RowTypeSpec]> = [
  [Alias.CALENDAR_LIST, CALENDAR_LIST],
  ["timeline", TIMELINE],
  [Layout.TIMELINE_COLUMN, TIMELINE_COLUMN_LAYOUT],
  [Layout.TIMELINE_ROW, TIMELINE_ROW_LAYOUT],
  [Layout.GRID, GRID_LAYOUT],
  [Layout.CANVAS, CANVAS_LAYOUT],
  [Layout.CALENDAR_BODY, CALENDAR_BODY_LAYOUT],
  ["list", LIST],
  ["calendar-system", CALENDAR_SYSTEM],
  [Alias.WEATHER, WEATHER],
  [Source.DATA, DATA],
  [Source.DATA_V1, DATA_V1],
  [Alias.TIMEZONE, TIMEZONE],
  [Source.STICKER, STICKER],
  [Inert.SURFACE, SURFACE],
  [Source.TEXT, TEXT],
  [Source.NUMBER, NUMBER],
  [Source.CHECKMARK, CHECKMARK],
  [Source.FORMULA, FORMULA],
  [Source.TASKS, TASKS],
  [Inert.LABEL, LABEL],
  [Source.LOGS, LOGS],
  [Source.VIDEO, VIDEO],
];

/**
 * Populate `ROW_TYPE_SPECS` with every built-in entry, then freeze it.
 * Throws if any entry is already registered (by design — the registry is
 * static).
 *
 * Called once, as a side effect at module load. Package-internal: a test
 * that needs its own specs layers them on the client it builds
 * (`SheetRegistries.extend`).
 */
function registerBuiltinRowTypeSpecs(): void {
  for (const [wireType, spec] of BUILTIN_ENTRIES) {
    rowTypeSpecRegistry.register(wireType, spec);
  }
  rowTypeSpecRegistry.freeze();
}

// Side effect: populate the registry once per module load.
registerBuiltinRowTypeSpecs();
